import { diffDays, toParisDate } from './dates.js';
import type { MeetingKey, PhaseKey } from './planning.js';
import { companyNameSimilarity, normalizeCompanyName, SIMILAR_NAME_THRESHOLD } from './quoteDraft.js';

/**
 * Lot BR : logique pure de la page Projets (`docs/maquette/projets-0211.html`) : colonne du Suivi d'un projet,
 * état de ses rendez-vous, encart « À regarder », proposition de client par nom proche. Le serveur s'en sert pour
 * `GET /api/projects/board` et pour tenir `projects.status` cohérent ; rien n'est écrit ici.
 */

/** Seuils du Suivi et de « À regarder » : le seul endroit où ils sont écrits. */
export const BOARD_THRESHOLDS = {
  /** « Sans nouvelles » : aucun échange ni étape cochée depuis tant de jours. */
  quietDays: 14,
  /** « Sans nouvelles » : en attente client depuis tant de jours. */
  waitingDays: 7,
  /** Colonne « Terminé » : projets finis depuis moins de tant de jours, le reste derrière un bouton. */
  doneRecentDays: 30,
  /** Vue Rendez-vous : colonne « À fixer » pour les plages qui ouvrent dans tant de jours. */
  meetingSoonDays: 30,
} as const;

export const BOARD_COLUMNS = ['start', 'waiting', 'mockup', 'site', 'admin', 'done'] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];

export const BOARD_COLUMN_LABELS: Record<BoardColumn, string> = {
  start: 'À démarrer',
  waiting: 'En attente client',
  mockup: 'Maquette en cours',
  site: 'Site en cours',
  admin: 'Finalisation administrative',
  done: 'Terminé',
};

export const BOARD_COLUMN_HINTS: Record<BoardColumn, string> = {
  start: 'Acompte, R3 ou infos à recevoir',
  waiting: 'À la main, une fois le mail envoyé',
  mockup: 'Infos reçues',
  site: 'Maquette validée',
  admin: 'En ligne, reste solde, abonnement, avis…',
  done: '100 % des étapes',
};

const rank = (column: BoardColumn): number => BOARD_COLUMNS.indexOf(column);

/** Libellé d'étape comparable : sans accents, sans casse, espaces réduits (« R5 · Présentation » → « r5 · presentation »). */
export function normalizeStepLabel(label: string): string {
  return label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
}

/** Ce dont les règles ont besoin d'une étape (étapes retirées déjà écartées). */
export interface BoardStep {
  id: string;
  label: string;
  phase: PhaseKey | null;
  done: boolean;
}

/**
 * Jalons reconnus dans les libellés. Les gabarits ont changé plusieurs fois (« Récupération des infos » et
 * « Récupération des informations (Photos…) », « R5 · formation », « R5 · Présentation du site », « R5 · rendu du
 * site », « Rendu du site »…) : on reconnaît le début du libellé, et sans libellé connu sur le projet on se replie
 * sur la phase de l'étape.
 */
const IS_INFOS = /^recuperation des inf/;
const IS_MOCKUP_SENT = /^maquette envoyee/;
const IS_FEEDBACK_DONE = /^retours integres/;
const IS_MOCKUP_OK = /^maquette validee/;
const IS_ONLINE = /^mise en ligne/;
const IS_SITE_WORK = /^creation du site/;

const matching = (steps: readonly BoardStep[], pattern: RegExp): BoardStep[] =>
  steps.filter((s) => pattern.test(normalizeStepLabel(s.label)));
const anyDone = (steps: readonly BoardStep[], pattern: RegExp): boolean =>
  matching(steps, pattern).some((s) => s.done);
const inPhaseDone = (steps: readonly BoardStep[], phases: readonly PhaseKey[]): boolean =>
  steps.some((s) => s.done && s.phase !== null && phases.includes(s.phase));

export interface AutoColumnInput {
  steps: readonly BoardStep[];
  projectType: 'evolusite' | 'partner';
  phases: Partial<Record<PhaseKey, string | null>>;
  today: string;
}

/**
 * Colonne donnée par les étapes cochées (sans tenir compte d'un placement à la main). « En attente client » n'en
 * sort jamais : elle ne se pose qu'à la main.
 * - Terminé : 100 % des étapes (et au moins une) ;
 * - Finalisation administrative : « Mise en ligne » cochée ;
 * - Site en cours : « Maquette validée » cochée ; un projet partenaire dès que son intégration commence ;
 * - Maquette en cours : « Récupération des infos » cochée (ou une étape qui la suppose : maquette envoyée, retours) ;
 * - À démarrer sinon.
 */
export function autoColumn({ steps, projectType, phases, today }: AutoColumnInput): BoardColumn {
  if (steps.length > 0 && steps.every((s) => s.done)) return 'done';
  // Replis : le jalon n'existe pas sur ce projet (étape renommée ou retirée), sa phase le remplace.
  const online = matching(steps, IS_ONLINE);
  if (online.length > 0 ? online.some((s) => s.done) : inPhaseDone(steps, ['R5'])) return 'admin';
  if (projectType === 'partner') {
    if (phases.Integration && phases.Integration <= today) return 'site';
    if (anyDone(steps, IS_SITE_WORK) || anyDone(steps, IS_MOCKUP_OK)) return 'site';
    return 'start';
  }
  const mockupOk = matching(steps, IS_MOCKUP_OK);
  if (mockupOk.length > 0 ? mockupOk.some((s) => s.done) : inPhaseDone(steps, ['Integration', 'R5']))
    return 'site';
  if (anyDone(steps, IS_INFOS) || anyDone(steps, IS_MOCKUP_SENT) || anyDone(steps, IS_FEEDBACK_DONE))
    return 'mockup';
  if (
    matching(steps, IS_INFOS).length === 0 &&
    inPhaseDone(steps, ['Maquette', 'Ajustements', 'R4', 'Retours'])
  )
    return 'mockup';
  return 'start';
}

/**
 * Colonne affichée : le placement à la main d'abord ; un projet au statut DONE est dans « Terminé » même s'il lui
 * reste une étape (projets anciens importés), comme le veut `projects.status`.
 */
export function effectiveColumn(input: {
  manual: BoardColumn | null;
  status: string;
  auto: BoardColumn;
}): BoardColumn {
  if (input.manual) return input.manual;
  return input.status === 'DONE' ? 'done' : input.auto;
}

/**
 * Ce que change une étape cochée, décochée, ajoutée ou retirée. Un placement à la main tient jusqu'à ce qu'une étape
 * fasse avancer le projet au-delà ; rien ne recule tout seul : si les étapes redescendent, la carte reste où elle
 * était (un placement à la main la retient là).
 */
export function columnAfterSteps(input: {
  manual: BoardColumn | null;
  status: string;
  before: BoardColumn;
  after: BoardColumn;
}): { manual: BoardColumn | null } {
  const { manual, status, before, after } = input;
  if (rank(after) > rank(before)) {
    if (manual && rank(after) <= rank(manual)) return { manual: manual === after ? null : manual };
    return { manual: null };
  }
  if (rank(after) < rank(before) && !manual && status !== 'DONE') return { manual: before };
  return { manual: manual === after ? null : manual };
}

/** Placement à la main dans la colonne `target` : rien à retenir si les étapes y mènent déjà (`auto`). */
export function manualColumnFor(target: BoardColumn, auto: BoardColumn): BoardColumn | null {
  return target === auto ? null : target;
}

/** `projects.status` suit « Terminé » : DONE dans cette colonne ; à la sortie, selon les dates (jamais DONE). */
export function statusForColumn(
  column: BoardColumn,
  current: string,
  hasStarted: boolean,
): 'DONE' | 'ACTIVE' | 'PLANNED' | 'PAUSED' {
  if (column === 'done') return 'DONE';
  if (current !== 'DONE') return current as 'ACTIVE' | 'PLANNED' | 'PAUSED';
  return hasStarted ? 'ACTIVE' : 'PLANNED';
}

/* ------------------------------------------------------------------ rendez-vous */

export const BOARD_MEETING_KEYS: readonly MeetingKey[] = ['R3', 'R4', 'R5'];

/** `done` fait, `set` fixé à venir, `past` fixé et passé (étape pas cochée), `tofix` à fixer, `late` à fixer en retard. */
export type BoardMeetingState = 'done' | 'set' | 'past' | 'tofix' | 'late';

export interface BoardMeetingInput {
  phases: Partial<Record<PhaseKey, string | null>>;
  meetings: Partial<
    Record<
      MeetingKey,
      {
        windowStart: string;
        windowEnd: string;
        at: string | null;
        /** Lot BQ : déjà dépassé par une étape cochée, sans date : ni plage, ni « à fixer », ni alerte. */
        passed?: boolean | undefined;
        /** Lot BQ : `agenda` = date reprise d'un événement de l'agenda. */
        source?: 'agenda' | undefined;
      }
    >
  >;
  steps: readonly BoardStep[];
}

export interface BoardMeeting {
  key: MeetingKey;
  state: BoardMeetingState;
  /** Horodatage du rendez-vous fixé. */
  at: string | null;
  /** Date du rendez-vous fixé (à Paris), sinon la date prévue au planning. */
  date: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  /** « Fixer… » possible : une plage existe et le rendez-vous n'est pas posé (lot K). */
  canFix: boolean;
  /** À fixer bientôt ou en retard : colonne « À fixer » de la vue Rendez-vous. */
  soon: boolean;
  /** Étape qui dit que le rendez-vous a eu lieu (« Cocher » de la vue Rendez-vous). */
  stepId: string | null;
  /** Date reprise d'un événement de l'agenda (la fiche le dit). */
  fromAgenda: boolean;
}

const PHASE_ORDER: readonly PhaseKey[] = [
  'R3',
  'Maquette',
  'Ajustements',
  'R4',
  'Retours',
  'Integration',
  'R5',
];
const MEETING_STEP: Record<MeetingKey, RegExp[]> = {
  R3: [/^r3\b/],
  // Aucun gabarit n'a d'étape « R4 … » : c'est « Maquette validée » qui dit que la présentation a eu lieu.
  R4: [/^r4\b/, /^maquette validee/],
  R5: [/^r5\b/, /^rendu du site/],
};

/** Étape d'un rendez-vous sur ce projet, ou null (étape retirée, ancien gabarit). */
export function meetingStep(steps: readonly BoardStep[], key: MeetingKey): BoardStep | null {
  for (const pattern of MEETING_STEP[key]) {
    const found = matching(steps, pattern)[0];
    if (found) return found;
  }
  return null;
}

/** État d'un rendez-vous du projet ; null : pas de ce rendez-vous sur ce projet (partenaire sans R3). */
export function boardMeeting(
  project: BoardMeetingInput,
  key: MeetingKey,
  today: string,
): BoardMeeting | null {
  const meeting = project.meetings[key];
  const planned = project.phases[key] ?? null;
  if (!meeting && !planned) return null;
  const step = meetingStep(project.steps, key);
  const laterDone = project.steps.some(
    (s) => s.done && s.phase !== null && PHASE_ORDER.indexOf(s.phase) > PHASE_ORDER.indexOf(key),
  );
  // Sans plage ou sans étape connue, une étape cochée plus loin vaut pour ce rendez-vous (projets importés).
  const done = !!step?.done || ((!meeting || !step) && laterDone) || (!!meeting?.passed && !meeting.at);
  const atDate = meeting?.at ? toParisDate(new Date(meeting.at)) : null;
  const windowEnd = meeting?.windowEnd ?? null;
  const windowStart = meeting?.windowStart ?? null;
  let state: BoardMeetingState = 'tofix';
  if (done) state = 'done';
  else if (atDate) state = atDate < today ? 'past' : 'set';
  else if (windowEnd ? windowEnd < today : !!planned && planned < today) state = 'late';
  const opening = windowStart ?? planned;
  const soon =
    state === 'late' ||
    (state === 'tofix' && !!opening && diffDays(today, opening) <= BOARD_THRESHOLDS.meetingSoonDays);
  return {
    key,
    state,
    at: meeting?.at ?? null,
    date: atDate ?? planned,
    windowStart,
    windowEnd,
    canFix: !!meeting && !meeting.at && state !== 'done',
    soon,
    stepId: step?.id ?? null,
    fromAgenda: meeting?.source === 'agenda',
  };
}

/* ------------------------------------------------------------------ « À regarder » */

export const ALERT_GROUPS = ['late', 'tick', 'quiet', 'link'] as const;
export type AlertGroup = (typeof ALERT_GROUPS)[number];

export const ALERT_GROUP_LABELS: Record<AlertGroup, { title: string; hint: string }> = {
  late: { title: 'En retard', hint: 'Dates dépassées' },
  tick: { title: 'À cocher', hint: 'Rendez-vous passés, étape pas cochée' },
  quiet: {
    title: 'Sans nouvelles',
    hint: `${BOARD_THRESHOLDS.quietDays} j sans échange ni étape cochée, ou ${BOARD_THRESHOLDS.waitingDays} j d’attente client`,
  },
  link: { title: 'À relier', hint: 'Aucun client Axonaut' },
};

export interface ProjectAlert {
  group: AlertGroup;
  level: 'bad' | 'warn';
  /** Rendez-vous concerné (badge), s'il y en a un. */
  meeting: MeetingKey | null;
  /** Phrase entière (fiche, vue détaillée). */
  text: string;
  /** Précision courte de la ligne de l'encart (« à fixer, 28/10 »). */
  short: string;
  /** Une seule alerte de la vue simple (« R3 en retard »). */
  summary: string;
}

const dayMonth = (date: string): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

export interface AlertInput extends BoardMeetingInput {
  column: BoardColumn;
  clientId: string | null;
  /** Date du dernier échange avec le client (null : aucun connu). */
  lastExchangeOn: string | null;
  /** Date de la dernière étape cochée (null : aucune connue). */
  lastStepDoneOn: string | null;
  /** Date de passage en attente client (colonne `waiting`). */
  waitingSince: string | null;
}

/** Alertes d'un projet, dans l'ordre de gravité. Un projet terminé n'en a aucune. */
export function projectAlerts(project: AlertInput, today: string): ProjectAlert[] {
  if (project.column === 'done') return [];
  const out: ProjectAlert[] = [];
  const online = project.phases.R5 ?? null;
  const onlineDue = !!online && online < today && !anyDone(project.steps, IS_ONLINE);
  if (onlineDue) {
    out.push({
      group: 'late',
      level: 'bad',
      meeting: null,
      text: `Mise en ligne prévue le ${dayMonth(online)}, pas cochée`,
      short: `en ligne ${dayMonth(online)}`,
      summary: 'Mise en ligne dépassée',
    });
  }
  for (const key of BOARD_MEETING_KEYS) {
    const meeting = boardMeeting(project, key, today);
    if (!meeting) continue;
    if (meeting.state === 'late' && !(key === 'R5' && onlineDue)) {
      const limit = meeting.windowEnd ?? meeting.date;
      out.push({
        group: 'late',
        level: 'bad',
        meeting: key,
        text: `${key} à fixer${meeting.windowEnd ? ` avant le ${dayMonth(meeting.windowEnd)}` : ''}, date passée`,
        short: limit ? `à fixer, ${dayMonth(limit)}` : 'à fixer',
        summary: `${key} en retard`,
      });
    }
    if (meeting.state === 'past' && meeting.date) {
      out.push({
        group: 'tick',
        level: 'warn',
        meeting: key,
        text: `${key} du ${dayMonth(meeting.date)} passé, étape pas cochée`,
        short: `le ${dayMonth(meeting.date)}`,
        summary: `${key} à cocher`,
      });
    }
  }
  const waitingDays =
    project.column === 'waiting' && project.waitingSince ? diffDays(project.waitingSince, today) : null;
  if (waitingDays !== null && waitingDays >= BOARD_THRESHOLDS.waitingDays) {
    out.push({
      group: 'quiet',
      level: 'warn',
      meeting: null,
      text: `En attente client depuis ${waitingDays} j`,
      short: `attente ${waitingDays} j`,
      summary: `Attente client ${waitingDays} j`,
    });
  }
  if (!project.clientId) {
    out.push({
      group: 'link',
      level: 'warn',
      meeting: null,
      text: 'Aucun client Axonaut relié',
      short: '',
      summary: 'Client à relier',
    });
  } else if (waitingDays === null || waitingDays < BOARD_THRESHOLDS.waitingDays) {
    // Déjà signalé en attente client : pas une seconde ligne « Sans nouvelles » pour le même projet.
    const last = [project.lastExchangeOn, project.lastStepDoneOn]
      .filter((d): d is string => !!d)
      .sort()
      .at(-1);
    const silent = last ? diffDays(last, today) : null;
    if (silent !== null && silent >= BOARD_THRESHOLDS.quietDays) {
      out.push({
        group: 'quiet',
        level: 'warn',
        meeting: null,
        text: `Aucun échange ni étape cochée depuis ${silent} j`,
        short: `${silent} j`,
        summary: `Sans nouvelles ${silent} j`,
      });
    }
  }
  return out;
}

/** Terminé depuis moins de `doneRecentDays` jours ? Sans date connue : rangé avec les anciens. */
export function isRecentlyDone(finishedOn: string | null, today: string): boolean {
  return !!finishedOn && diffDays(finishedOn, today) < BOARD_THRESHOLDS.doneRecentDays;
}

/* ------------------------------------------------------------------ client par nom proche */

/**
 * Clients dont le nom ressemble à celui du projet, le plus proche d'abord. Suggestion seulement : jamais de
 * rattachement automatique par le nom (règle ferme de Maxime). Ce qui est entre parenthèses compte à part : « Lacertus
 * (Amilex) » est proposé pour « LACERTUS ».
 */
export function suggestClients<T extends { name: string }>(
  projectName: string,
  clients: readonly T[],
  limit = 3,
): T[] {
  const name = normalizeCompanyName(projectName);
  const withoutNote = (value: string): string => value.replace(/\(.*?\)/g, ' ');
  return clients
    .map((client) => {
      const exact =
        normalizeCompanyName(client.name) === name ||
        normalizeCompanyName(withoutNote(client.name)) === normalizeCompanyName(withoutNote(projectName));
      return { client, exact, score: companyNameSimilarity(projectName, client.name) };
    })
    .filter((s) => s.exact || s.score >= SIMILAR_NAME_THRESHOLD)
    .sort(
      (a, b) =>
        Number(b.exact) - Number(a.exact) ||
        b.score - a.score ||
        a.client.name.length - b.client.name.length ||
        a.client.name.localeCompare(b.client.name),
    )
    .slice(0, limit)
    .map((s) => s.client);
}

/** « 12 rue X, 85000 Ville » (ou sans virgule) découpé pour Axonaut ; sans code postal, tout va dans la rue. */
export function splitAddress(value: string): { street?: string; zipCode?: string; city?: string } {
  const text = value.trim().replace(/\s+/g, ' ');
  if (!text) return {};
  const found = /^(.*?)[,\s]*\b(\d{5})\s+(.+)$/.exec(text);
  if (!found) return { street: text };
  const street = (found[1] ?? '').trim();
  return { ...(street ? { street } : {}), zipCode: found[2]!, city: (found[3] ?? '').trim() };
}
